import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { parseListingImportCsv, createListingImportRow } from '../../util/api';
import { storableError } from '../../util/errors';
import { loadListingCapContextThunk } from '../ManageListingsPage/ManageListingsPage.duck';

export const parseImportCsvThunk = createAsyncThunk(
  'app/ImportListingsPage/parse',
  async ({ formData }, { rejectWithValue }) => {
    try {
      return await parseListingImportCsv(formData);
    } catch (e) {
      return rejectWithValue({
        code: e.code,
        message: e.message,
        status: e.status,
        column: e.column,
      });
    }
  }
);

export const createImportRowThunk = createAsyncThunk(
  'app/ImportListingsPage/createRow',
  async (body, { rejectWithValue }) => {
    try {
      return await createListingImportRow(body);
    } catch (e) {
      return rejectWithValue(storableError(e) || { code: e.code, message: e.message });
    }
  }
);

export const parseImportCsv = (file, defaults, config) => dispatch => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('defaults', JSON.stringify(defaults));
  formData.append('listingTypes', JSON.stringify(config?.listing?.listingTypes || []));
  formData.append('listingFields', JSON.stringify(config?.listing?.listingFields || []));
  formData.append('categories', JSON.stringify(config?.categoryConfiguration?.categories || []));
  formData.append('currency', config?.currency || 'USD');
  return dispatch(parseImportCsvThunk({ formData })).unwrap();
};

export const createImportRow = body => dispatch => dispatch(createImportRowThunk(body)).unwrap();

const importListingsPageSlice = createSlice({
  name: 'ImportListingsPage',
  initialState: {
    listingCap: null,
    shopDefaults: null,
    imported: [],
    possibleDuplicates: [],
    notImported: [],
    parseInProgress: false,
    parseError: null,
    createInProgress: false,
    createProgress: null,
  },
  reducers: {
    resetImportResults: state => {
      state.imported = [];
      state.possibleDuplicates = [];
      state.notImported = [];
      state.parseError = null;
      state.createProgress = null;
    },
    setShopDefaults: (state, action) => {
      state.shopDefaults = action.payload;
    },
    appendImported: (state, action) => {
      state.imported.push(action.payload);
    },
    appendNotImported: (state, action) => {
      state.notImported.push(action.payload);
    },
    setPossibleDuplicates: (state, action) => {
      state.possibleDuplicates = action.payload;
    },
    setCreateProgress: (state, action) => {
      state.createProgress = action.payload;
      state.createInProgress = !!action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadListingCapContextThunk.fulfilled, (state, action) => {
        state.listingCap = action.payload;
      })
      .addCase(parseImportCsvThunk.pending, state => {
        state.parseInProgress = true;
        state.parseError = null;
      })
      .addCase(parseImportCsvThunk.fulfilled, (state, action) => {
        state.parseInProgress = false;
        state.imported = [];
        state.possibleDuplicates = action.payload?.possibleDuplicates || [];
        state.notImported = action.payload?.notImported || [];
      })
      .addCase(parseImportCsvThunk.rejected, (state, action) => {
        state.parseInProgress = false;
        state.parseError = action.payload;
      });
  },
});

export const {
  resetImportResults,
  setShopDefaults,
  appendImported,
  appendNotImported,
  setPossibleDuplicates,
  setCreateProgress,
} = importListingsPageSlice.actions;

export const loadData = (params, search, config) => dispatch => {
  return dispatch(fetchCurrentUser()).then(() =>
    dispatch(loadListingCapContextThunk({ config })).catch(() => null)
  );
};

export default importListingsPageSlice.reducer;
